import { useTranslation } from "react-i18next";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../context/SnackbarContext";
import { SnackbarType } from "../components/sharedComponents/Snackbar";
import { LocationApplicationsBundleInput } from "../components/supportFunctions/graphql/ApplicationBundleQueries";
import { ApplicationBundle } from "../components/routeComponents/LocationsNew/LocationsManageApplications";
import { ApolloError, DocumentNode, gql, useMutation } from "@apollo/client";
import {
  GET_CUSTOMER_APPLICATION_BUNDLES,
  GET_LOCATION_APPLICATION_BUNDLES,
} from "./useGetApplicationBundles";

export enum ApplicationBundlesActions {
  CREATE = "create",
}

const CREATE_APPLICATION_BUNDLE = gql`
  mutation handleAddApplicationBundlesToLocation(
    $input: ApplicationBundlesForLocationInput!
  ) {
    setAssociatedApplicationBundlesForLocation(input: $input)
  }
`;

const useApplicationBundleMutation = () => {
  const createMutation = useMutation(
    CREATE_APPLICATION_BUNDLE,
    ResponseHandling("location_application_bundle_set_success", [
      GET_CUSTOMER_APPLICATION_BUNDLES,
      GET_LOCATION_APPLICATION_BUNDLES,
    ])
  );

  const mutate = async (
    action: ApplicationBundlesActions,
    input: ApplicationBundle[],
    locationId: string
  ) => {
    switch (action) {
      case ApplicationBundlesActions.CREATE: {
        const createInput = mapFormToInput(input, locationId);

        const [create] = createMutation;

        return await create({ variables: { input: createInput } });
      }

      default: {
        throw new Error(`Invalid action: ${action}`);
      }
    }
  };

  return {
    mutations: {
      mutate,
      isLoading: createMutation[1].loading,
    },
  };
};

function mapFormToInput(
  input: ApplicationBundle[],
  locationId: string
): LocationApplicationsBundleInput {
  return {
    locationId: locationId,
    applicationBundles: input.map((bundle) => {
      return {
        applicationBundleId: bundle.id,
        applyToDescendants: bundle.applyToDescendants,
      };
    }),
  };
}

function ResponseHandling(messageKey: string, queries: Array<DocumentNode>) {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  return {
    refetchQueries: queries,
    onCompleted: () => {
      showSnackbar(SnackbarType.Success, t(messageKey));
    },
    onError: (error: ApolloError) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
    update: (cache) => {
      cache.evict({ id: "ROOT_QUERY", fieldName: "customer" });
      cache.gc();
    },
  };
}

export { useApplicationBundleMutation };
